import React, { useState } from "react";
import { Button, MenuItem, Stack, TextField, Typography } from "@mui/material";

export default function UserSignInChallenge({
  step,
  onConfirm,
  onCancel,
  onResend,
  busy,
}) {
  const [response, setResponse] = useState("");
  const [attributes, setAttributes] = useState({});
  const name = step.signInStep;
  const choices = step.allowedMFATypes;
  const selection =
    name === "CONTINUE_SIGN_IN_WITH_MFA_SELECTION" ||
    name === "CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION";
  const newPassword = name === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED";
  const emailSetup = name === "CONTINUE_SIGN_IN_WITH_EMAIL_SETUP";
  const totpSetup = name === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP";
  const signup = name === "CONFIRM_SIGN_UP";
  const missing = newPassword ? step.missingAttributes || [] : [];
  const label = selection
    ? "Verification method"
    : newPassword
      ? "New password"
      : emailSetup
        ? "Verification email"
        : "Verification code";
  const methodLabels = {
    SMS: "Text message",
    EMAIL: "Email",
    TOTP: "Authenticator app",
  };

  return (
    <Stack
      component="form"
      spacing={2}
      sx={{ mt: 2 }}
      onSubmit={(event) => {
        event.preventDefault();
        if (
          response.trim() &&
          missing.every((key) => attributes[key]?.trim())
        ) {
          onConfirm(response, attributes);
        }
      }}
    >
      {totpSetup && (
        <Typography component="code" sx={{ overflowWrap: "anywhere" }}>
          {step.totpSetupDetails?.sharedSecret}
        </Typography>
      )}
      <TextField
        autoFocus
        required
        fullWidth
        label={label}
        value={response}
        disabled={busy}
        select={selection}
        type={newPassword ? "password" : emailSetup ? "email" : "text"}
        autoComplete={
          newPassword ? "new-password" : emailSetup ? "email" : "one-time-code"
        }
        onChange={(event) => setResponse(event.target.value)}
      >
        {selection &&
          (choices || []).map((method) => (
            <MenuItem key={method} value={method}>
              {methodLabels[method] || method}
            </MenuItem>
          ))}
      </TextField>
      {missing.map((attribute) => (
        <TextField
          key={attribute}
          required
          label={attribute.replace(/_/g, " ")}
          disabled={busy}
          value={attributes[attribute] || ""}
          onChange={(event) =>
            setAttributes({ ...attributes, [attribute]: event.target.value })
          }
        />
      ))}
      <Button
        type="submit"
        variant="contained"
        disabled={busy || !response.trim()}
      >
        Continue
      </Button>
      <Button onClick={onCancel} disabled={busy}>
        Back to sign in
      </Button>
      {signup && (
        <Button onClick={onResend} disabled={busy}>
          Resend verification code
        </Button>
      )}
    </Stack>
  );
}

export function signInChallengeMessage(step) {
  switch (step.signInStep) {
    case "CONFIRM_SIGN_UP":
      return "Confirm your email with the code sent when you registered.";
    case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
      return "Choose a new password to finish signing in.";
    case "CONTINUE_SIGN_IN_WITH_EMAIL_SETUP":
      return "Enter the email address where you want to receive verification codes.";
    case "CONTINUE_SIGN_IN_WITH_MFA_SELECTION":
    case "CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION":
      return "Choose how to verify your sign-in.";
    case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
      return "Add the setup key below to your authenticator app, then enter the code it generates.";
    default:
      return step.codeDeliveryDetails?.destination
        ? `Enter the code sent to ${step.codeDeliveryDetails.destination}.`
        : "Enter your verification code to finish signing in.";
  }
}
